/**
 * BTKoopblok — Budgetthing A6 Cash Stuffing configurator
 * Shopify Online Store 2.0 section (scoped per section instance)
 */

/**
 * Wording for the lines this file rewrites while the customer configures.
 * The section settings override every one of these; they stay here so a
 * blanked-out setting still says something sensible.
 */
const BT_TEKSTEN = {
  voorraad_op: 'Uitverkocht. Kies een andere kleur.',
  voorraad_krap: 'Nog maar {n} op voorraad | OP=OP',
  voorraad_vaag: 'Nog enkele op voorraad | OP=OP',
  teller: '{n} van {max}',
  cat_leeg: 'Kies zelf welke thema\'s je dashboards krijgen.',
  cat_nog: 'Nog {n} te kiezen.',
  cat_verplicht: 'Kies eerst je 3 dashboards. Nog {n} te kiezen.',
  cat_vol: 'Je {max} dashboards zitten erin. Nog een dashboard nodig? {prijs} per stuk.',
  cat_extra: '{n} {woord} {bedrag}',
  cat_ev: 'extra dashboard',
  cat_mv: 'extra dashboards',
  cat_pak: 'Je krijgt er {krijg} voor {bedrag} — voordeliger dan {n} los.',
  los_ipv: 'in plaats van {bedrag}',
  env_tekort: 'Je hebt {n} enveloppen nodig: {cat} voor je dashboards en {ch} om mee te challengen.',
  env_vulaan: 'Vul {n} aan',
  env_leeg: '{env} enveloppen zitten erin: {cat} voor je dashboards en {ch} voor je challenges.' +
    ' Je wisselt van challenge wanneer je wilt. Wil je er meer tegelijk doen, voeg dan enveloppen toe.',
  env_aantal: '{n} extra enveloppen {bedrag}',
  env_pak: 'Je krijgt er {krijg} voor {bedrag} — goedkoper dan {n} los.',
  kleur_geen: 'Dit pakket bevat geen map',
  kleur_note: 'A6-formaat · zit in Compleet en Met planner',
  korting: '{n}% korting',
  bespaar: 'Je bespaart {bedrag}',
  prijs_los: 'Los bestel je deze onderdelen voor {bedrag}.',
  verzend_nog: 'Nog {bedrag} tot gratis verzending',
  verzend_ok: 'Gratis verzending gehaald',
  fout_config: 'Kan dit pakket niet toevoegen. Controleer of alle producten correct zijn ingesteld.',
  fout_cart: 'Fout bij het toevoegen aan winkelwagen. Controleer je internetverbinding en probeer opnieuw.',
};

class BTKoopblok {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.section;
    this.bundleId = null;
    this.order = [];
    this.env = 0;
    this.fmt = 'A6';
    this.selectedColor = null;
    this.tierIndex = 1;
    this.upsell = false;
    this.gratisEerder = null;
    this.meldingWacht = null;
    this.meldingWeg = null;
    this.meldingOp = null;

    // Parse JSON data blob
    const jsonScriptId = 'bt-prijsdata-' + this.sectionId;
    const jsonScript = document.getElementById(jsonScriptId);
    if (!jsonScript) {
      console.error('BTKoopblok: missing data JSON script', jsonScriptId);
      return;
    }

    try {
      this.prijsdata = JSON.parse(jsonScript.textContent);
    } catch (e) {
      console.error('BTKoopblok: failed to parse JSON data', e);
      return;
    }

    // Cache DOM references
    this.els = {
      tiers: Array.from(this.container.querySelectorAll('.tier')),
      chips: Array.from(this.container.querySelectorAll('.chip')),
      steps: Array.from(this.container.querySelectorAll('.step')),
      sws: Array.from(this.container.querySelectorAll('.sw')),
      teller: this.container.querySelector('.teller'),
      cathint: this.container.querySelector('.cathint'),
      addonhint: this.container.querySelector('.addonhint'),
      addonSub: this.container.querySelector('.addon__s'),
      stepnum: this.container.querySelector('.stepnum'),
      colorbar: this.container.querySelector('.colorbar'),
      kleurnaam: this.container.querySelector('.kleurnaam'),
      binderwrap: this.container.querySelector('.binderwrap'),
      binder: this.container.querySelector('.binder'),
      binderfoto: this.container.querySelector('.binderfoto'),
      stockbar: this.container.querySelector('.stockbar'),
      kopNu: this.container.querySelector('[data-kop-nu]'),
      kopWas: this.container.querySelector('[data-kop-was]'),
      kopBes: this.container.querySelector('[data-kop-bes]'),
      priceDisplay: this.container.querySelector('.price .big'),
      wasDisplay: this.container.querySelector('.price .was'),
      offDisplay: this.container.querySelector('.price .off'),
      noteDisplay: this.container.querySelector('.note'),
      mobctaNu: this.container.querySelector('.mobcta__nu'),
      mobctaWas: this.container.querySelector('.mobcta__was'),
      ship: this.container.querySelector('.ship'),
      shipBar: this.container.querySelector('.ship__f'),
      shipText: this.container.querySelector('.ship__t'),
      ctaButtons: Array.from(this.container.querySelectorAll('.cta')),
      colorbarNote: this.container.querySelector('.colorbar__note'),
      ups: this.container.querySelector('[data-ups]'),
      upsSw: this.container.querySelector('[data-ups-sw]'),
      upsW: this.container.querySelector('[data-ups-w]'),
      upsMeer: this.container.querySelector('[data-ups-meer]'),
      upsDlg: this.container.querySelector('[data-ups-dlg]'),
      melding: this.container.querySelector('[data-melding]'),
      accLijst: this.container.querySelector('[data-acc-lijst]'),
      accWarn: this.container.querySelector('[data-acc-warn]'),
    };

    this.init();
  }

  init() {
    // Set up event listeners
    this.els.tiers.forEach((tier, idx) => {
      tier.addEventListener('click', () => this.selectTier(idx));
      tier.addEventListener('keydown', (e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          this.selectTier(idx);
        }
      });
    });

    this.els.chips.forEach((chip) => {
      chip.addEventListener('click', () => this.toggleChip(chip));
    });

    this.els.steps.forEach((step) => {
      step.addEventListener('click', () => {
        const delta = parseInt(step.dataset.d, 10);
        this.env = Math.max(0, Math.min(20, this.env + delta));
        this.render();
      });
    });

    this.els.sws.forEach((sw) => {
      sw.addEventListener('click', () => this.selectColor(sw));
    });

    this.els.ctaButtons.forEach((btn) => {
      btn.addEventListener('click', () => this.addToCart());
    });

    if (this.els.upsSw) {
      this.els.upsSw.addEventListener('change', () => {
        this.upsell = this.els.upsSw.checked;
        this.render();
      });
    }

    if (this.els.upsMeer && this.els.upsDlg) {
      this.els.upsMeer.addEventListener('click', () => this.els.upsDlg.showModal());
    }

    this.container.querySelectorAll('.acc-item').forEach((item) => {
      const head = item.querySelector('.acc-head');
      if (head) {
        head.addEventListener('click', () => item.classList.toggle('open'));
      }
    });

    // Paired per panel rather than by document order, so several guarantee
    // blocks each open their own small print.
    this.container.querySelectorAll('.garantie').forEach((paneel) => {
      const knop = paneel.querySelector('[data-garantie-i]');
      const meer = paneel.querySelector('[data-garantie-meer]');
      if (!knop || !meer) return;

      knop.addEventListener('click', () => {
        const openen = meer.hidden;
        meer.hidden = !openen;
        knop.setAttribute('aria-expanded', openen ? 'true' : 'false');
      });
    });

    // Initialize UI
    this.selectTier(1);
    setTimeout(() => {
      if (this.els.sws.length > 0) {
        // The section marks the colour to open on — the first one that is
        // actually in stock. Only fall back to the first swatch if it marked
        // none, otherwise the page can open on a sold-out colour.
        const start =
          this.els.sws.find((sw) => sw.getAttribute('aria-checked') === 'true') ||
          this.els.sws[0];
        this.selectColor(start);
      }
      this.render();

      const nuLaden = () => this.laadFotosVoor();
      if (window.requestIdleCallback) {
        window.requestIdleCallback(nuLaden, { timeout: 3000 });
      } else {
        setTimeout(nuLaden, 1200);
      }
    }, 0);

    // Hook for theme editor reloads
    window.addEventListener('shopify:section:load', (e) => {
      if (e.detail.sectionId === this.sectionId) {
        this.render();
      }
    });
  }

  selectTier(idx) {
    this.tierIndex = idx;
    this.els.tiers.forEach((t, i) => {
      const isSelected = i === idx;
      t.classList.toggle('is-sel', isSelected);
      t.setAttribute('aria-checked', isSelected ? 'true' : 'false');
    });
    // De voorraadmelding hangt aan het pakket: zonder map is er geen kleur die
    // op kan raken. Zonder dit bleef de melding van het vorige pakket staan.
    this.showStock(this.selectedColor);
    this.render();
  }

  toggleChip(chip) {
    const i = this.order.indexOf(chip);
    if (i >= 0) {
      this.order.splice(i, 1);
    } else {
      this.order.push(chip);
    }
    this.render();
  }

  selectColor(sw) {
    this.selectedColor = sw;
    this.els.sws.forEach((s) => {
      s.setAttribute('aria-checked', s === sw ? 'true' : 'false');
    });
    const colorName = sw.dataset.naam || 'Kleur';
    this.els.kleurnaam.textContent = colorName;
    const hexColor = sw.dataset.hex || '#000000';
    this.els.binderwrap.style.color = hexColor;
    this.toonFoto(sw.dataset.foto, colorName);
    this.showStock(sw);
    this.render();
  }

  /**
   * The photo of the variant in this colour, with the drawn binder as the
   * stand-in for a colour that has no photo yet.
   */
  toonFoto(url, colorName) {
    if (!this.els.binderfoto) return;

    if (url) {
      this.els.binderfoto.src = url;
      this.els.binderfoto.alt = colorName;
    }
    this.els.binderfoto.hidden = !url;

    // hidden is a property of HTMLElement, not of SVGElement: assigning it on
    // an <svg> sets a plain JS property and never reaches the attribute, so the
    // drawing has to be toggled through the attribute itself.
    if (!this.els.binder) return;
    if (url) {
      this.els.binder.setAttribute('hidden', '');
    } else {
      this.els.binder.removeAttribute('hidden');
    }
  }

  /**
   * Fetch the other colours' photos once the page has settled, so switching
   * colour swaps the picture instantly instead of showing an empty frame.
   */
  laadFotosVoor() {
    const urls = this.els.sws
      .map((sw) => sw.dataset.foto)
      .filter((url) => url && url !== this.els.binderfoto?.getAttribute('src'));

    urls.forEach((url) => {
      const img = new Image();
      img.src = url;
    });
  }

  /**
   * pakDeal(pakketten, aantal) — find cheapest combo of pack sizes
   * @param {Array} pakketten - [[size, price], [size, price], ...]
   * @param {number} aantal - minimum quantity needed
   * @returns {Object} {kost, krijg, stuks} - total cost, total quantity
   *   received, and how many of each pack (same order as pakketten)
   */
  pakDeal(pakketten, aantal) {
    const kost = [0];
    const krijg = [0];
    const keus = [-1];

    for (let i = 1; i <= aantal; i++) {
      kost[i] = Infinity;
      krijg[i] = i;
      keus[i] = -1;

      for (let j = 0; j < pakketten.length; j++) {
        const [size, price] = pakketten[j];
        const rest = Math.max(0, i - size);
        if (kost[rest] + price < kost[i] - 0.0001) {
          kost[i] = kost[rest] + price;
          krijg[i] = krijg[rest] + size;
          keus[i] = j;
        }
      }
    }

    // Welke pakken samen dat bedrag maken: terugrekenen vanaf het eind. De
    // winkelwagen moet precies deze pakken krijgen, anders betaalt de klant
    // iets anders dan de pagina belooft.
    const stuks = pakketten.map(() => 0);
    let i = aantal;
    while (i > 0 && keus[i] >= 0) {
      stuks[keus[i]] += 1;
      i = Math.max(0, i - pakketten[keus[i]][0]);
    }

    return { kost: kost[aantal], krijg: krijg[aantal], stuks };
  }

  /** De pakken die bij een aantal horen, als regels voor de winkelwagen. */
  pakRegels(packs, aantal) {
    if (!packs || !packs.length || aantal <= 0) return [];
    const deal = this.pakDeal(packs.map((v) => [v.n || 1, v.prijs || 0]), aantal);
    return packs
      .map((v, j) => ({ id: v.id, quantity: deal.stuks[j] }))
      .filter((r) => r.quantity > 0);
  }

  eur(n) {
    return '€' + n.toFixed(2).replace('.', ',');
  }

  /**
   * Vult een bespaarplaatje: het bedrag dat je bespaart, met het percentage
   * klein ernaast. Zonder voordeel blijft het hele plaatje weg.
   */
  zetBespaar(el, bedrag, pct) {
    if (!el) return;
    const toon = pct > 0 && bedrag >= 0.005;
    el.hidden = !toon;
    if (!toon) return;
    el.querySelector('b').textContent = this.tekst('bespaar', { bedrag: this.eur(bedrag) });
    el.querySelector('i').textContent = this.tekst('korting', { n: pct });
  }

  /**
   * A label from the section settings with its {placeholders} filled in.
   * An empty setting falls back to the built-in wording rather than to an
   * empty line on the page.
   */
  tekst(sleutel, waarden) {
    const uitSettings = (this.prijsdata.teksten || {})[sleutel];
    let uit = uitSettings && uitSettings.length ? uitSettings : BT_TEKSTEN[sleutel] || '';

    Object.keys(waarden || {}).forEach((k) => {
      uit = uit.split('{' + k + '}').join(waarden[k]);
    });

    return uit;
  }

  getSelectedTier() {
    return this.els.tiers[this.tierIndex];
  }

  showStock(sw) {
    if (!sw || !this.selectedColor) return;

    const stockVal = parseInt(this.selectedColor.dataset.voorraad || 'NaN', 10);
    const tier = this.getSelectedTier();
    const heeftMap = tier.dataset.t !== '0';
    const stockThreshold = 5;

    const isOp = heeftMap && !isNaN(stockVal) && stockVal <= 0;
    this.setButtonsDisabled(isOp);

    // Een pakket zonder map heeft geen kleur die op kan raken.
    if (!heeftMap) {
      this.els.stockbar.hidden = true;
      return;
    }

    if (isNaN(stockVal) || stockVal > stockThreshold) {
      this.zetVaag(stockVal);
      return;
    }

    this.els.stockbar.hidden = false;
    this.els.stockbar.classList.remove('stockbar--vaag');
    this.els.stockbar.classList.toggle('uitverkocht', isOp);
    const widthPct = Math.max(6, Math.min(100, (stockVal / stockThreshold) * 100));
    this.els.stockbar.querySelector('.stockbar__f').style.width = widthPct + '%';

    const stockText = isOp
      ? this.tekst('voorraad_op')
      : this.tekst('voorraad_krap', { n: stockVal });
    this.els.stockbar.querySelector('.stockbar__t').textContent = stockText;
  }

  /**
   * Een kleur die geen "nog maar {n}" verdient, krijgt de vage melding: geen
   * getal, dus ook geen balk.
   *
   * De tekst komt hier niet via tekst() binnen, want die valt bij een leeg
   * veld terug op de standaardtekst. Juist bij deze melding is een leeg veld
   * bedoeld als "laat maar weg".
   */
  zetVaag(stockVal) {
    const bar = this.els.stockbar;
    const teksten = this.prijsdata.teksten || {};
    const regel = 'voorraad_vaag' in teksten
      ? teksten.voorraad_vaag
      : BT_TEKSTEN.voorraad_vaag;

    // Boven de ingestelde grens blijft het stil: "nog enkele" naast een volle
    // voorraad is geen schaarste maar een onwaarheid. 0 = altijd tonen.
    const tot = parseInt(this.prijsdata.vaag_tot, 10) || 0;
    const binnenGrens = tot === 0 || (!isNaN(stockVal) && stockVal <= tot);
    const toon = !!regel && binnenGrens;

    bar.hidden = !toon;
    bar.classList.toggle('stockbar--vaag', toon);
    bar.classList.remove('uitverkocht');
    if (toon) bar.querySelector('.stockbar__t').textContent = regel;
  }

  setButtonsDisabled(disabled) {
    this.els.ctaButtons.forEach((btn) => {
      btn.disabled = disabled;
      btn.classList.toggle('is-op', disabled);
    });
  }

  render() {
    const tier = this.getSelectedTier();
    const MAX_CHIPS = 3;

    // Update chip states
    this.els.chips.forEach((chip) => {
      const isIncluded = this.order.indexOf(chip) >= 0;
      const isExtra = isIncluded && this.order.indexOf(chip) >= MAX_CHIPS;
      chip.dataset.state = isExtra ? 'extra' : isIncluded ? 'incl' : '';
      chip.setAttribute('aria-pressed', isIncluded ? 'true' : 'false');
    });

    // Update tier summary and per-tier price
    this.els.tiers.forEach((t) => {
      const own = (t.dataset.sum || '').trim();
      if (own) {
        t.querySelector('.tier__sum').textContent = own;
      } else {
        const kort = Array.from(t.querySelectorAll('.inc[data-kort]'))
          .map((r) => r.dataset.kort)
          .join(' · ');
        t.querySelector('.tier__sum').textContent = kort;
      }

      const { nu, was } = this.getTierPrice(t);
      const tierKorting = was > nu ? Math.round((1 - nu / was) * 100) : 0;

      t.querySelector('.tier__nu').textContent = this.eur(nu);

      const streep = t.querySelector('.tier__pr s');
      streep.textContent = this.eur(was);
      streep.hidden = tierKorting <= 0;

      this.zetBespaar(t.querySelector('.tier__besrij'), was - nu, tierKorting);
    });

    // Update chip counter
    const incl = Math.min(this.order.length, MAX_CHIPS);
    const extra = Math.max(0, this.order.length - MAX_CHIPS);
    this.els.teller.textContent = this.tekst('teller', { n: incl, max: MAX_CHIPS });
    this.els.teller.classList.toggle('vol', incl === MAX_CHIPS);
    this.els.cathint.classList.toggle('vol', extra > 0);

    // Is er al eens op de knop gedrukt zonder genoeg thema's, dan blijft de
    // waarschuwing staan en telt hij mee af, tot het er drie zijn.
    const verplicht = this.els.cathint.classList.contains('is-verplicht');
    if (incl >= MAX_CHIPS) this.els.cathint.classList.remove('is-verplicht');

    if (incl < MAX_CHIPS && verplicht) {
      this.els.cathint.textContent = this.tekst('cat_verplicht', { n: MAX_CHIPS - incl });
    } else if (incl < MAX_CHIPS) {
      this.els.cathint.textContent = this.order.length === 0
        ? this.tekst('cat_leeg')
        : this.tekst('cat_nog', { n: MAX_CHIPS - incl });
    } else if (extra === 0) {
      // The real per-pack price, not a guessed one — so a price change in
      // Shopify shows up here instead of quietly drifting out of sync.
      this.els.cathint.textContent = this.tekst('cat_vol', {
        max: MAX_CHIPS,
        prijs: this.eur(this.losPrijs('dashboard', 1) / 100),
      });
    } else {
      this.els.cathint.textContent = this.staffelRegel('dashboard', extra, 'cat_pak', 'cat_extra', {
        woord: this.tekst(extra === 1 ? 'cat_ev' : 'cat_mv'),
      });
    }

    // Update stepper
    this.els.stepnum.textContent = this.env;
    this.els.steps.forEach((step) => {
      step.disabled = step.dataset.d === '-1' && this.env === 0;
    });

    // The 5 included envelopes cover 3 dashboards plus 2 challenges side by side.
    // Running more challenges at once is possible, but needs extra envelopes.
    const ENV_INCL = 5;
    const CHALLENGES_TEGELIJK = 2;
    const envNodig = this.order.length + CHALLENGES_TEGELIJK;
    const tekort = envNodig - ENV_INCL - this.env;

    if (tekort > 0) {
      this.els.addonhint.classList.add('let');

      // Built as nodes rather than an HTML string: the sentence comes from a
      // section setting, and an & or < in it would break an innerHTML build.
      const knop = document.createElement('button');
      knop.type = 'button';
      knop.className = 'vul';
      knop.textContent = this.tekst('env_vulaan', { n: tekort });
      knop.addEventListener('click', () => {
        this.env += tekort;
        this.render();
      });

      this.els.addonhint.replaceChildren(
        this.tekst('env_tekort', {
          n: envNodig,
          cat: this.order.length,
          ch: CHALLENGES_TEGELIJK,
        }) + ' ',
        knop
      );
    } else {
      this.els.addonhint.classList.remove('let');
      if (this.env === 0) {
        this.els.addonhint.textContent = this.tekst('env_leeg', {
          env: ENV_INCL,
          cat: MAX_CHIPS,
          ch: CHALLENGES_TEGELIJK,
        });
      } else {
        this.els.addonhint.textContent =
          this.staffelRegel('envelope', this.env, 'env_pak', 'env_aantal');
      }
    }

    // Update color picker disabled state
    const geenMap = tier.dataset.t === '0';
    this.els.colorbar.classList.toggle('is-off', geenMap);
    this.els.sws.forEach((sw) => {
      sw.disabled = geenMap;
    });
    this.els.colorbarNote.textContent = geenMap
      ? this.tekst('kleur_geen')
      : this.tekst('kleur_note');

    // The accordion's "Wat ontvang je?" list mirrors whichever tier is
    // selected — the rows already carry their final, resolved text (the
    // {f}-placeholder is filled in above), so this just reads them back.
    if (this.els.accLijst) {
      const punten = Array.from(tier.querySelectorAll('.inc__t')).map((el) => {
        const li = document.createElement('li');
        li.textContent = el.textContent;
        return li;
      });
      this.els.accLijst.replaceChildren(...punten);
    }
    if (this.els.accWarn) {
      this.els.accWarn.hidden = !geenMap;
    }

    // Update prices (all in cents, then convert to euros)
    const tierPrices = this.getTierPrice(tier);
    const extraDashboards = Math.max(0, this.order.length - MAX_CHIPS);

    // Build pakket arrays from variant data (format: [[size, price], ...])
    const dashPakket = (this.prijsdata.dashPak || []).map(v => [v.n || 1, v.prijs || 0]);
    const envPakket = (this.prijsdata.envPak || []).map(v => [v.n || 1, v.prijs || 0]);

    const extraDashPrice = extraDashboards > 0 ?
      (dashPakket.length > 0 ? this.pakDeal(dashPakket, extraDashboards).kost : extraDashboards * 150) : 0;
    const envPrice = this.env > 0 ?
      (envPakket.length > 0 ? this.pakDeal(envPakket, this.env).kost : this.env * 180) : 0;

    // The price of one loose unit, found the same order-independent way as
    // the real price — indexing [0] assumed the smallest pack came first in
    // Shopify's variant order, which nothing guarantees.
    const dashboardUnit = dashPakket.length > 0 ? this.pakDeal(dashPakket, 1).kost : 150;
    const envUnit = envPakket.length > 0 ? this.pakDeal(envPakket, 1).kost : 180;

    const upsellPrijs = this.prijsdata.upsell ? this.prijsdata.upsell.prijs / 100 : 0;
    const upsellDeel = this.upsell ? upsellPrijs : 0;

    // The price block describes the configured set only; the upsell rides along
    // as its own line, so adding it can never move the set's discount.
    const nu = tierPrices.nu + (extraDashPrice / 100) + (envPrice / 100);
    const was = tierPrices.was + (extraDashboards * dashboardUnit / 100) + (this.env * envUnit / 100);

    const priceStr = this.eur(nu);
    const wasStr = this.eur(was);
    const discount = was > 0 ? Math.round((1 - nu / was) * 100) : 0;

    const voordeel = discount > 0;

    // De prijs bovenaan is geen tweede waarheid: het zijn dezelfde bedragen
    // als hieronder bij de knop, ze staan alleen eerder in beeld.
    if (this.els.kopNu) {
      this.els.kopNu.textContent = priceStr;
      this.els.kopWas.textContent = wasStr;
      this.els.kopWas.hidden = !voordeel;
      this.zetBespaar(this.els.kopBes, was - nu, discount);
    }

    // Het verhaal onder de kolommen noemt hetzelfde bedrag. Dat staat in een
    // andere sectie en dus buiten dit blok: vandaar door het document heen, en
    // elke keer opnieuw opgezocht, want de theme editor kan die sectie
    // onderweg vervangen.
    document.querySelectorAll('[data-bt-prijs]').forEach((el) => {
      el.textContent = priceStr;
    });
    document.querySelectorAll('[data-bt-was]').forEach((el) => {
      el.textContent = wasStr;
      el.hidden = !voordeel;
    });
    document.querySelectorAll('[data-bt-bes]').forEach((el) => {
      this.zetBespaar(el, was - nu, discount);
    });

    this.els.priceDisplay.textContent = priceStr;
    this.els.wasDisplay.textContent = wasStr;
    this.els.wasDisplay.hidden = !voordeel;
    this.zetBespaar(this.els.offDisplay, was - nu, discount);
    this.els.noteDisplay.textContent = this.tekst('prijs_los', { bedrag: wasStr });
    this.els.noteDisplay.hidden = !voordeel;
    this.els.mobctaNu.textContent = priceStr;
    this.els.mobctaWas.textContent = wasStr;
    this.els.mobctaWas.hidden = !voordeel;

    // Update shipping bar
    // Shipping counts everything that goes to the cart, upsell included.
    const GRATIS = (this.prijsdata.gratis_vanaf || 7500) / 100;
    const totaal = nu + upsellDeel;
    const rest = GRATIS - totaal;
    const pct = Math.min(100, (totaal / GRATIS) * 100);
    this.els.shipBar.style.width = pct + '%';
    this.els.ship.classList.toggle('vol', rest <= 0);
    this.els.shipText.textContent = rest > 0
      ? this.tekst('verzend_nog', { bedrag: this.eur(rest) })
      : this.tekst('verzend_ok');

    const gratis = rest <= 0;
    this.toonUpsell(GRATIS - nu, upsellPrijs, gratis);

    // Only on the crossing itself, and never on the first render, so arriving
    // on a page that is already over the threshold stays quiet.
    if (this.gratisEerder === false && gratis) {
      const bron = this.els.ups && !this.els.ups.hidden ? this.els.ups : this.els.ship;
      if (bron) btConfetti(bron, this.prijsdata.confetti);
      // A beat after the confetti, so the two do not compete for attention.
      clearTimeout(this.meldingWacht);
      this.meldingWacht = setTimeout(() => this.toonMelding(), 1000);
    }
    this.gratisEerder = gratis;
  }

  /** Announce free shipping in the middle of the screen, with a bar counting it away. */
  toonMelding() {
    const el = this.els.melding;
    if (!el) return;

    clearTimeout(this.meldingWeg);
    clearTimeout(this.meldingOp);

    const sec = parseFloat(el.dataset.sec) || 4;
    el.style.setProperty('--duur', sec + 's');
    el.hidden = false;

    // Dropping the class and reading a layout property restarts the bar's
    // animation, so a second unlock does not show a bar that is already spent.
    el.classList.remove('is-aan');
    void el.offsetWidth;
    el.classList.add('is-aan');

    this.meldingWeg = setTimeout(() => {
      el.classList.remove('is-aan');
      this.meldingOp = setTimeout(() => {
        el.hidden = true;
      }, 250);
    }, sec * 1000);
  }

  /**
   * Offer the upsell only while it actually closes the gap to free shipping —
   * a product cheaper than the shortfall would promise something it can't keep.
   */
  toonUpsell(tekort, prijs, gratis) {
    if (!this.els.ups) return;

    // Once switched on it stays in view whatever happens to the gap — the
    // switch is the only way back off.
    const past = prijs > 0 && tekort > 0 && tekort <= prijs;
    this.els.ups.hidden = !(this.upsell || past);
    if (this.els.ups.hidden) return;

    this.els.ups.classList.toggle('is-aan', this.upsell);
    this.els.upsSw.checked = this.upsell;

    const regels = this.els.upsW.dataset;
    this.els.upsW.textContent = this.upsell
      ? (gratis ? regels.gratis : regels.aan)
      : regels.uit;
  }

  /**
   * Fill a tier's inclusion rows with their loose price and return the bundle
   * price alongside the sum of those rows — the "los besteld" comparison.
   */
  getTierPrice(tier) {
    const tierIdx = parseInt(tier.dataset.t, 10);
    const tierName = ['Zonder map', 'Compleet', 'Met planner'][tierIdx];
    const inTier = (this.prijsdata.pakket || []).filter((v) => v.pakket === tierName);

    const colorName = this.selectedColor && this.selectedColor.dataset.naam;
    const variant = inTier.length
      ? inTier.find((v) => v.kleur === colorName) ||
        inTier.reduce((a, b) => (b.prijs < a.prijs ? b : a))
      : null;

    let was = 0;
    tier.querySelectorAll('.inc[data-k]').forEach((row) => {
      const cents = this.losPrijs(row.dataset.k, parseInt(row.dataset.n, 10));
      was += cents;
      row.querySelector('s').textContent = this.eur(cents / 100);
      row.querySelector('.inc__t').textContent = row.dataset.tpl.replace('{f}', this.fmt);
    });

    const nu = variant ? variant.prijs / 100 : 0;
    return { nu, was: was > 0 ? was / 100 : nu };
  }

  /** Loose price in cents for one inclusion row. */
  /**
   * Cost in cents and how many you actually end up with. The two differ when
   * a bigger pack undercuts the exact number asked for — then the extras come
   * along for free, which is worth telling the customer.
   */
  losDeal(kind, aantal) {
    const packs = {
      dashboard: this.prijsdata.dashPak,
      sheet: this.prijsdata.sheetPak,
      envelope: this.prijsdata.envPak,
    }[kind];

    if (packs && packs.length) {
      return this.pakDeal(packs.map((v) => [v.n || 1, v.prijs || 0]), aantal);
    }

    const stuksprijs = {
      binder: this.prijsdata.binderPrijs,
      planner: this.prijsdata.plannerPrijs,
      challenge: this.prijsdata.challenge,
      card: this.prijsdata.card,
    }[kind];

    return { kost: (stuksprijs || 0) * aantal, krijg: aantal };
  }

  losPrijs(kind, aantal) {
    return this.losDeal(kind, aantal).kost;
  }

  /**
   * The line describing extras the customer added, in whichever of two shapes
   * the pack prices make true: a bigger pack that hands them more than they
   * asked for, or the plain count with what it would have cost per piece.
   */
  staffelRegel(kind, aantal, sleutelPak, sleutelAantal, extraWaarden) {
    const deal = this.losDeal(kind, aantal);
    const bedrag = this.eur(deal.kost / 100);

    if (deal.krijg > aantal) {
      return this.tekst(sleutelPak, { krijg: deal.krijg, bedrag, n: aantal });
    }

    let regel = this.tekst(sleutelAantal, Object.assign({ n: aantal, bedrag }, extraWaarden));

    // Only claim a saving when there is one to a whole cent, so rounding
    // cannot produce "€6,00 in plaats van €6,00".
    const losTotaal = aantal * this.losPrijs(kind, 1);
    if (losTotaal - deal.kost >= 1) {
      regel += ' ' + this.tekst('los_ipv', { bedrag: this.eur(losTotaal / 100) });
    }

    return regel;
  }

  /**
   * Get the variant matching the selected tier and color
   */
  getBundleVariant() {
    const tier = this.getSelectedTier();
    if (!tier) return null;

    // Zonder gekozen kleur blijft alleen een pakket zonder map over.
    const colorName = this.selectedColor ? this.selectedColor.dataset.naam : null;
    const tierName = this.getTierName();

    // Find matching variant in prijsdata.pakket.
    // Een pakket zonder map heeft geen kleur om uit te kiezen en dus één
    // variant zonder kleurnaam; die hoort bij elke gekozen kleur te passen.
    const match = this.prijsdata.pakket?.find(
      (v) =>
        v.pakket === tierName &&
        (v.kleur === null || v.kleur === colorName) &&
        v.beschikbaar
    );

    return match;
  }

  /**
   * Add to cart with Shopify cart API
   */
  async addToCart() {
    const tier = this.getSelectedTier();
    if (!tier) {
      console.error('BTKoopblok: no tier selected');
      return;
    }

    // Elk pakket heeft 3 dashboards. Zonder thema's weet niemand wat er in de
    // doos moet, dus eerst kiezen.
    if (this.order.length < 3) {
      this.vraagThemas();
      return;
    }

    const bundelVariant = this.getBundleVariant();
    const bundleVariantId = bundelVariant?.id || null;
    if (!bundleVariantId) {
      const tierName = this.getTierName();
      const colorName = this.selectedColor?.dataset.naam || '(geen kleur gekozen)';
      console.error('BTKoopblok: no bundle variant found for', { tierName, colorName });
      alert(this.tekst('fout_config'));
      return;
    }

    // Build cart items array
    const items = [];
    const bundleTimestamp = Date.now();

    // De gekozen thema's horen mee de bestelling in: zonder die namen weet
    // niemand welke dashboards er in de doos moeten. De eerste drie zitten in
    // het pakket, wat daarna komt staat op de losse regel eronder.
    const MAX_CHIPS = 3;
    const themas = this.order.map((chip) => chip.textContent.trim()).filter(Boolean);
    const inPakket = themas.slice(0, MAX_CHIPS).join(', ');

    // Add bundle
    // Een pakket zonder map heeft geen kleur: die regel dan ook niet op de
    // order, anders staat er een kleur bij iets wat niet meegaat.
    const bundelProps = { 'Pakket': this.getTierName() };
    if (bundelVariant.kleur !== null && this.selectedColor) {
      bundelProps['Kleur'] = this.selectedColor.dataset.naam;
    }
    if (inPakket) bundelProps['Dashboards'] = inPakket;
    bundelProps['_bundel'] = bundleTimestamp.toString();

    items.push({
      id: bundleVariantId,
      quantity: 1,
      properties: bundelProps,
    });

    // Add extra dashboards if selected
    const extra = Math.max(0, this.order.length - MAX_CHIPS);
    // Dezelfde pakken als waarmee de pagina de prijs uitrekende. De namen van
    // de thema's staan op de eerste regel, zodat ze er één keer bij staan.
    if (extra > 0) {
      const erbij = themas.slice(MAX_CHIPS).join(', ');
      this.pakRegels(this.prijsdata.dashPak, extra).forEach((regel, k) => {
        const extraProps = { '_bundel': bundleTimestamp.toString() };
        if (erbij && k === 0) extraProps['Dashboards'] = erbij;
        items.push({ ...regel, properties: extraProps });
      });
    }

    if (this.upsell && this.prijsdata.upsell) {
      items.push({
        id: this.prijsdata.upsell.id,
        quantity: 1,
        properties: {
          '_bundel': bundleTimestamp.toString(),
        },
      });
    }

    // Add extra envelopes if selected
    if (this.env > 0) {
      this.pakRegels(this.prijsdata.envPak, this.env).forEach((regel) => {
        items.push({ ...regel, properties: { '_bundel': bundleTimestamp.toString() } });
      });
    }

    // Send to cart
    try {
      // Een kenmerk op de winkelwagen: dat komt als "Aanvullende gegevens" op
      // de order te staan. Een thema kan zelf geen ordertag zetten; hiermee
      // kan Shopify Flow er een maken.
      //
      // De thema's in het pakket gaan hier ook mee. De Bundles-app splitst de
      // bundel bij het afrekenen in losse onderdelen, en de eigenschappen van
      // de bundelregel komen dan niet op de order (gezien bij #BT-22605).
      // Een kenmerk van de hele bestelling blijft wel staan.
      const kenmerken = {};
      const bron = (this.prijsdata.teksten || {}).bron;
      if (bron) kenmerken['Bron'] = bron;
      if (inPakket) kenmerken['Dashboards in pakket'] = inPakket;
      if (Object.keys(kenmerken).length) {
        try {
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: kenmerken }),
          });
        } catch (e) {
          // Zonder dit kenmerk mag de bestelling gewoon doorgaan.
          console.warn('BTKoopblok: kon het kenmerk niet zetten', e);
        }
      }

      console.log('BTKoopblok: adding to cart', items);
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (response.ok) {
        if (typeof window.upcartOpenCart === 'function') {
          // Refresh before opening, otherwise the drawer shows the pre-add cart.
          if (typeof window.upcartRefreshCart === 'function') {
            await window.upcartRefreshCart();
          }
          window.upcartOpenCart();
        } else {
          window.location.href = '/cart';
        }
      } else {
        console.error('BTKoopblok: cart add failed', response.status, response.statusText);
        alert(this.tekst('fout_cart'));
      }
    } catch (e) {
      // The raw message goes to the console, not to the customer — it says
      // nothing they can act on.
      console.error('BTKoopblok: cart add error', e);
      alert(this.tekst('fout_cart'));
    }
  }

  /**
   * Te weinig thema's gekozen: terug naar de keuze, met de reden erbij. De
   * knop blijft klikbaar — een uitgeschakelde knop zegt niet wat er mist.
   */
  vraagThemas() {
    const nog = 3 - this.order.length;
    const hint = this.els.cathint;
    hint.textContent = this.tekst('cat_verplicht', { n: nog });
    hint.classList.add('is-verplicht');

    const kop = this.container.querySelector('.lab--split') || hint;
    kop.scrollIntoView({ behavior: 'smooth', block: 'center' });

    // Toetsenbordgebruikers staan meteen bij de eerste vrije keuze.
    const vrij = this.els.chips.find((c) => c.getAttribute('aria-pressed') !== 'true');
    if (vrij) vrij.focus({ preventScroll: true });
  }

  getTierName() {
    const names = ['Zonder map', 'Compleet', 'Met planner'];
    return names[this.tierIndex] || 'Compleet';
  }

  getBundleVariantId() {
    const variant = this.getBundleVariant();
    return variant?.id || null;
  }


  disconnect() {
    // A pending notice would fire into a section that no longer exists.
    clearTimeout(this.meldingWacht);
    clearTimeout(this.meldingWeg);
    clearTimeout(this.meldingOp);
  }
}

const BT_ROL_MS = 460;

class BTAanbiedingsklok {
  constructor(el) {
    this.el = el;
    this.tekst = el.querySelector('[data-klok-tekst]');
    this.deadline = this.bepaalDeadline();

    // Size each reel to the opening value: a countdown only ever shrinks, so a
    // 120-day sale gets three digits now and never needs a fourth later.
    const opening = this.verdeel(this.resterend());
    this.cellen = {};
    el.querySelectorAll('[data-klok]').forEach((cel) => {
      const naam = cel.dataset.klok;
      const breedte = Math.max(2, String(opening[naam]).length);
      cel.textContent = '';
      this.cellen[naam] = [];
      for (let i = 0; i < breedte; i++) {
        const rol = this.maakRol();
        cel.appendChild(rol);
        this.cellen[naam].push(rol);
      }
    });

    this.tik();
    this.timer = setInterval(() => this.tik(), 1000);
  }

  /** A reel holding two descending 9-0 cycles, so a roll past 0 can keep going. */
  maakRol() {
    const rol = document.createElement('span');
    rol.className = 'bt-rol';

    const strip = document.createElement('span');
    strip.className = 'bt-rol__strip';
    for (let cyclus = 0; cyclus < 2; cyclus++) {
      for (let cijfer = 9; cijfer >= 0; cijfer--) {
        const vak = document.createElement('span');
        vak.className = 'bt-rol__d';
        vak.textContent = cijfer;
        strip.appendChild(vak);
      }
    }

    rol.appendChild(strip);
    rol.btIndex = 0;
    return rol;
  }

  /** Roll one reel to a digit, always downward, wrapping through the duplicate. */
  draai(rol, cijfer) {
    const strip = rol.firstElementChild;
    const huidig = rol.btIndex % 10;
    const doel = 9 - cijfer;
    if (doel === huidig) return;

    const naar = doel > huidig ? doel : doel + 10;
    rol.btIndex = naar;
    strip.style.setProperty('--bt-rol-i', naar);

    if (naar < 10) return;

    clearTimeout(rol.btSnap);
    rol.btSnap = setTimeout(() => {
      if (rol.btIndex !== naar) return; // a newer roll already took over
      strip.style.transition = 'none';
      rol.btIndex = doel;
      strip.style.setProperty('--bt-rol-i', doel);
      void strip.offsetHeight; // apply the jump before animation is allowed again
      strip.style.transition = '';
    }, BT_ROL_MS);
  }

  resterend() {
    return Math.max(0, Math.ceil((this.deadline - Date.now()) / 1000));
  }

  verdeel(seconden) {
    return {
      dagen: Math.floor(seconden / 86400),
      uren: Math.floor(seconden / 3600) % 24,
      minuten: Math.floor(seconden / 60) % 60,
      seconden: seconden % 60,
    };
  }

  bepaalDeadline() {
    if (this.el.dataset.timer === 'dagelijks') {
      const [uur, minuut] = (this.el.dataset.dagelijks || '').split(':');
      const deadline = new Date();
      deadline.setHours(this.getal(uur, 23, 23), this.getal(minuut, 59, 55), 0, 0);
      if (deadline.getTime() <= Date.now()) {
        deadline.setDate(deadline.getDate() + 1);
      }
      return deadline.getTime();
    }

    // Liquid worked out how many seconds were left when the page rendered, so a
    // misconfigured clock on the visitor's device cannot move the deadline.
    return Date.now() + (parseInt(this.el.dataset.rest, 10) || 0) * 1000;
  }

  tik() {
    if (!this.el.isConnected) {
      clearInterval(this.timer);
      return;
    }

    const resterend = this.resterend();
    if (resterend === 0) {
      clearInterval(this.timer);
      this.el.hidden = true;
      return;
    }

    const delen = this.verdeel(resterend);
    Object.keys(delen).forEach((naam) => this.zet(naam, delen[naam]));

    if (this.tekst) {
      this.tekst.textContent =
        `Nog ${delen.dagen} dagen, ${delen.uren} uren, ` +
        `${delen.minuten} minuten en ${delen.seconden} seconden`;
    }
  }

  zet(naam, waarde) {
    const rollen = this.cellen[naam];
    if (!rollen) return;

    const cijfers = String(waarde).padStart(rollen.length, '0').slice(-rollen.length);
    rollen.forEach((rol, i) => this.draai(rol, Number(cijfers[i])));
  }

  /** A whole number within range, or the fallback when the setting is unusable. */
  getal(waarde, max, terugval) {
    const tekst = String(waarde == null ? '' : waarde).trim();
    const n = Number(tekst);
    return tekst !== '' && Number.isInteger(n) && n >= 0 && n <= max ? n : terugval;
  }
}

const BT_SNIPPERS = ['#6E3B5C', '#C9952A', '#EADCE5', '#B695A0', '#8B8C6B'];

const BT_LOTTIE_SRC =
  'https://cdnjs.cloudflare.com/ajax/libs/lottie-web/5.12.2/lottie_light.min.js';

let btLottieLaden = null;

/** Fetch the player the first time it is actually needed, never on page load. */
function btLottie() {
  if (!btLottieLaden) {
    btLottieLaden = new Promise((klaar, mislukt) => {
      const script = document.createElement('script');
      script.src = BT_LOTTIE_SRC;
      script.onload = () => klaar(window.lottie);
      script.onerror = mislukt;
      document.head.appendChild(script);
    });
  }
  return btLottieLaden;
}

/** Play the confetti animation full screen, falling back to the drawn burst. */
function btConfetti(bron, animatie) {
  if (!animatie) {
    btConfettiCanvas(bron);
    return;
  }

  btLottie()
    .then((lottie) => {
      const laag = document.createElement('div');
      laag.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:9999';
      document.body.appendChild(laag);

      const speler = lottie.loadAnimation({
        container: laag,
        renderer: 'svg',
        loop: false,
        autoplay: true,
        path: animatie,
        rendererSettings: { preserveAspectRatio: 'xMidYMid slice' },
      });

      speler.addEventListener('complete', () => {
        speler.destroy();
        laag.remove();
      });
    })
    .catch(() => btConfettiCanvas(bron));
}

/** A short burst of confetti out of an element, on its own throwaway canvas. */
function btConfettiCanvas(bron) {
  const vak = bron.getBoundingClientRect();
  const breed = window.innerWidth;
  const hoog = window.innerHeight;
  const dpr = window.devicePixelRatio || 1;

  const doek = document.createElement('canvas');
  doek.width = breed * dpr;
  doek.height = hoog * dpr;
  doek.style.cssText =
    'position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:9999';
  document.body.appendChild(doek);

  const ctx = doek.getContext('2d');
  ctx.scale(dpr, dpr);

  const snippers = [];
  for (let i = 0; i < 90; i++) {
    const hoek = -Math.PI / 2 + (Math.random() - 0.5) * 1.7;
    const kracht = 6 + Math.random() * 7;
    snippers.push({
      x: vak.left + Math.random() * vak.width,
      y: vak.top + vak.height * 0.5,
      vx: Math.cos(hoek) * kracht,
      vy: Math.sin(hoek) * kracht,
      draai: Math.random() * Math.PI,
      tol: (Math.random() - 0.5) * 0.3,
      b: 5 + Math.random() * 5,
      h: 3 + Math.random() * 4,
      kleur: BT_SNIPPERS[i % BT_SNIPPERS.length],
    });
  }

  let frame = 0;
  (function teken() {
    ctx.clearRect(0, 0, breed, hoog);
    let levend = 0;

    snippers.forEach((s) => {
      s.vy += 0.22;
      s.vx *= 0.99;
      s.x += s.vx;
      s.y += s.vy;
      s.draai += s.tol;
      if (s.y > hoog + 20) return;
      levend++;

      ctx.save();
      ctx.translate(s.x, s.y);
      ctx.rotate(s.draai);
      ctx.globalAlpha = Math.max(0, 1 - frame / 150);
      ctx.fillStyle = s.kleur;
      ctx.fillRect(-s.b / 2, -s.h / 2, s.b, s.h);
      ctx.restore();
    });

    frame++;
    if (levend && frame < 170) {
      requestAnimationFrame(teken);
    } else {
      doek.remove();
    }
  })();
}

function btInit(wortel) {
  wortel.querySelectorAll('[data-bt-koopblok]').forEach((container) => {
    if (!container.BTKoopblok) {
      container.BTKoopblok = new BTKoopblok(container);
    }
  });

  wortel.querySelectorAll('[data-bt-banner]:not([data-timer="geen"])').forEach((banner) => {
    if (!banner.BTKlok) {
      banner.BTKlok = new BTAanbiedingsklok(banner);
    }
  });
}

document.addEventListener('DOMContentLoaded', () => btInit(document));

// Also initialize on shopify:section:load (theme editor)
document.addEventListener('shopify:section:load', (event) => btInit(event.target));